<template>
    <div class="layout">
        <aside class="sidebar" :class="{ collapsed: isCollapsed }">
            <ul>
                <li @click="toggleMenu">
                <img :src="menuIcon" alt="Toggle Menu" />
                <span>{{ isCollapsed ? 'Expandir' : 'Recolher' }}</span>
                </li>

                <li
                v-for="menu in menusTree"
                :key="menu.id"
                class="menu-item"
                @mouseover="openMenu(menu)"
                @mouseleave="closeMenu(menu)"
                @click="handleMenuClick(menu)"
                >
                <img :src="getIcon(menu.icone)" :alt="menu.titulo_menu" />
                <span>{{ menu.titulo_menu }}</span>

                <div
                    v-if="menu.tipo === 'GRUPO' && isMenuOpen(menu)"
                    class="submenu"
                    :class="{ relative: isCollapsed }"
                >
                    <ul>
                    <li
                        v-for="filho in menu.filhos"
                        :key="filho.id"
                        @click.stop="selectMenuItem(filho)"
                    >
                        <span>{{ filho.titulo_menu }}</span>
                    </li>
                    </ul>
                </div>
                </li>

                <li class="logout" @click="confirmLogout">
                <img :src="getIcon('menuLogout')" alt="Logout" />
                <span>Desconectar</span>
                </li>
            </ul>
        </aside>

        <main class="content">
            <header class="header">
                <div class="header-title">{{ nmTitulo || globalStore.nmperfil }}</div>

                <div style="align-items: center; display: flex;">
                <div class="user-info">
                    <div class="user-name">{{ globalStore.nmusuario }}</div>
                    <div class="establishment-name">{{ globalStore.nmestabelecimento || globalStore.nmsim || globalStore.nmep }}</div>
                </div>
                <div class="token-info">{{ timeRemaining }}</div>
                </div>
            </header>

            <div class="main-content">
                <slot>
                    <component
                        :is="activeComponent"
                        :key="activeComponentKey"
                        v-bind="{
                            ...activeComponentProps,
                            ...activeComponentEvents
                        }"
                        @close="closeActiveComponent"
                    />
                </slot>
            </div>
        </main>
    </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { Api } from '@/funcoes/apiExport';
import { componentRegistry } from '@/config/menuComponentRegistry';
import { globalStore } from '@/globals';
import { iconRegistry } from '@/config/menuIconRegistry';
import { jwtDecode } from 'jwt-decode';
import { useRouter } from 'vue-router';

const pilhaNavegacao = ref([]);
const router = useRouter();

const isCollapsed = ref(true);
const nmTitulo = ref(null);
const menuAtivo = ref(null);
const menusApi = ref([]);
const openedGroupId = ref(null);

const timeRemaining = ref(null);
let timer = null;



const menuIcon = computed(() =>
    isCollapsed.value ? iconRegistry.menuExpand : iconRegistry.menuCollapse
);

const getIcon = (icone) => {
    if (!icone) return iconRegistry.default;
    return iconRegistry[icone] || iconRegistry.default;
};

/* =========================
   NORMALIZAÇÃO DO MENU
   ========================= */

const menusTree = computed(() => {
    const ativos = (menusApi.value || [])
        .filter(x => x.ativo === 'S')
        .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

    const grupos = ativos
        .filter(x => x.tipo === 'GRUPO')
        .map(grupo => ({
        ...grupo,
        filhos: ativos
            .filter(x => x.idmenu_pai === grupo.id && x.tipo === 'ITEM')
            .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
        }));

    const itensRaiz = ativos.filter(
        x => x.tipo === 'ITEM' && (x.idmenu_pai === null || x.idmenu_pai === undefined)
    );

    return [...grupos, ...itensRaiz].sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
});

/* =========================
   COMPONENTE ATIVO
   ========================= */

const activeComponent = computed(() => {
    const nome = menuAtivo.value?.componente;

    if (nome && componentRegistry[nome]) {
        return componentRegistry[nome];
    }

    return globalStore.showAcoesEstabelecimento
        ? componentRegistry.EstabelecimentoDashPage
        : componentRegistry.ResumoGeralPage;
});

const activeComponentKey = computed(() => {
    const menu = menuAtivo.value;

    if (!menu) return 'ResumoGeralPage';

    return `${menu.id}-${menu.componente}`;
});

const activeComponentProps = computed(() => {
    const menu = menuAtivo.value;
    if (!menu?.props) return {};

    return parseMenuProps(menu.props);
});

const activeComponentEvents = computed(() => {
    const nome = menuAtivo.value?.componente;

    if (nome === 'EstabelecimentosRtTabPage') {
        return {
            onSimAlterado: handleSimAlterado
        };
    }

    return {};
});

function parseMenuProps(props) {
    if (!props) return {};

    if (typeof props === 'object') {
        return props;
    }

    try {
        return JSON.parse(props);
    } catch (e) {
        console.error('Props inválidas:', props);
        return {};
    }
}

/* =========================
   MENU
   ========================= */

const toggleMenu = () => {
    isCollapsed.value = !isCollapsed.value;
};

const openMenu = (menu) => {
    if (menu.tipo === 'GRUPO') openedGroupId.value = menu.id;
};

const closeMenu = (menu) => {
    if (menu.tipo === 'GRUPO' && openedGroupId.value === menu.id) {
        openedGroupId.value = null;
    }
};

const isMenuOpen = (menu) => openedGroupId.value === menu.id;

const handleMenuClick = (menu) => {
    if (menu.tipo === 'ITEM') {
        selectMenuItem(menu);
    }
};

const selectMenuItem = (item, registrarHistorico = true) => {
    // Ao escolher pelo menu principal, limpa navegação interna anterior
    if (registrarHistorico) {
        pilhaNavegacao.value = [];

        window.history.pushState({
            internal: true,
            menuId: item.id
        }, '');
    }

    menuAtivo.value = item;
    nmTitulo.value = item.titulo_pagina || item.titulo_menu || null;
    openedGroupId.value = null;
};

const closeActiveComponent = () => {
    voltarInterno();
};

/* =========================
   API MENUS
   ========================= */

async function carregarMenus() {
    try {
        const r = await Api.genericPost('/main/usuario/menus', {
            idusuario: globalStore.idusuario,
            idsim: globalStore.idsim});
        menusApi.value = Array.isArray(r) ? r : (r.lsMenus || []);
    } catch (error) {
        console.error('Erro ao carregar menus:', error);
        menusApi.value = [];
    }
}

async function handleSimAlterado() {
    await carregarMenus();

    menuAtivo.value = null;
    nmTitulo.value = null;
    openedGroupId.value = null;
}

/* =========================
   LOGIN / TOKEN
   ========================= */

const confirmLogout = () => {
    resetLogin();
};

const resetLogin = () => {
    Api.logoutAPI();
    router.push('/login');
};

const updateTimeRemaining = () => {
    const token = localStorage.getItem('jwtToken');

    if (token) {
        try {
            const decodedToken = jwtDecode(token);
            const expiration = decodedToken.exp * 1000;
            const now = Date.now();
            const difference = expiration - now;

            if (difference > 0) {
                const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
                const minutes = Math.floor((difference / (1000 * 60)) % 60);
                const seconds = Math.floor((difference / 1000) % 60);

                timeRemaining.value = [
                hours.toString().padStart(2, '0'),
                minutes.toString().padStart(2, '0'),
                seconds.toString().padStart(2, '0')
                ].join(':');
            } else {
                timeRemaining.value = 'Token expirado';
                clearInterval(timer);
            }
        } catch (error) {
            console.error('Erro ao decodificar o token:', error);
            timeRemaining.value = 'Erro ao verificar o token';
            clearInterval(timer);
        }
    } else {
        timeRemaining.value = 'Token não encontrado';
        clearInterval(timer);
    }
}


function voltarInterno() {
    if (pilhaNavegacao.value.length > 0) {
        const anterior = pilhaNavegacao.value.pop();

        menuAtivo.value = anterior.menu;
        nmTitulo.value = anterior.titulo;

        return true;
    }

    if (menuAtivo.value) {
        menuAtivo.value = null;
        nmTitulo.value = null;
        pilhaNavegacao.value = [];

        return true;
    }

    return false;
}

function onBrowserBack() {
    const voltou = voltarInterno();

    if (!voltou) {
        window.history.pushState({ internal: true }, '');
    }
}

/* =========================
   CICLO DE VIDA
   ========================= */

onMounted(async () => {
    window.history.replaceState({ internal: true }, '');

    await carregarMenus();
    updateTimeRemaining();

    timer = setInterval(updateTimeRemaining, 1000);

    window.addEventListener('popstate', onBrowserBack);
});

onBeforeUnmount(() => {
    clearInterval(timer);
    window.removeEventListener('popstate', onBrowserBack);
});
</script>